import { useState } from 'react'
import { useWorkBreaks } from 'queries/work'
import { QueryData } from 'queries/types'
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Loading,
  Message,
  Modal,
  Text,
} from '@workwhile/ui'
import { AlertCircleIcon, AlertTriangleIcon } from 'lucide-react'
import { getShiftTimezone } from 'lib/shift'
import { TimelineShift } from 'api/shift'
import { formatInTimeZone } from 'date-fns-tz'

type WorkBreak = QueryData<typeof useWorkBreaks>['breaks'][number]

const DATE_TIME_FORMAT = 'MMM dd, yyyy h:mm a zzz'
const TIME_FORMAT = 'h:mm a zzz'

const BREAK_ISSUE_LABEL: Record<NonNullable<WorkBreak['issue']>, string> = {
  required_not_recorded: 'Missing meal break information',
  in_progress: 'Break is still in progress',
  required_not_taken: 'Worker did not take the break.',
}

function getBreakHeading(workBreak: WorkBreak, hasMultipleBreaks: boolean) {
  if (!hasMultipleBreaks) {
    return workBreak.breakType === 'rest' ? 'Rest Break' : 'Meal Break'
  }
  if (workBreak.breakNumber === 1) {
    return 'First Break'
  }
  if (workBreak.breakNumber === 2) {
    return 'Second Break'
  }
  return `Break No. ${workBreak.breakNumber}`
}

function BreakDetail({
  workBreak,
  timezone,
}: {
  workBreak: WorkBreak
  timezone: string
}) {
  const formatTime = (value: string, format: string) =>
    formatInTimeZone(new Date(value), timezone, format)

  if (
    workBreak.status === 'taken' &&
    workBreak.startTime &&
    workBreak.endTime
  ) {
    return (
      <Text>
        Taken {formatTime(workBreak.startTime, DATE_TIME_FORMAT)} to{' '}
        {formatTime(workBreak.endTime, TIME_FORMAT)}
      </Text>
    )
  }

  if (workBreak.status === 'taking' && workBreak.startTime) {
    return (
      <Text>
        Started {formatTime(workBreak.startTime, DATE_TIME_FORMAT)}, no end time
        recorded
      </Text>
    )
  }

  if (workBreak.status === 'waived') {
    return <Text>Waived Break</Text>
  }

  if (workBreak.status === 'missed') {
    return <Text>Missed Break</Text>
  }

  if (workBreak.status === undefined && workBreak.takeBefore) {
    return (
      <Text>
        Required to take before{' '}
        {formatTime(workBreak.takeBefore, DATE_TIME_FORMAT)}{' '}
        {workBreak.waivePolicy ? '(waivable)' : ''}
      </Text>
    )
  }

  return null
}

export function MealBreaksModal({
  shift,
  workId,
}: {
  shift: TimelineShift
  workId: number
}) {
  const [visible, setVisible] = useState(false)
  const { data = { breaks: [], isSelfClockedOut: false }, isLoading } =
    useWorkBreaks(workId, {
      enabled: visible,
    })
  const { isSelfClockedOut } = data
  // Hide optional breaks the worker never recorded; they need no attention
  const breaks = data.breaks.filter(
    (workBreak) => workBreak.status !== undefined || workBreak.issue
  )
  const hasUnrecordedBreak = breaks.some(
    (workBreak) => workBreak.issue === 'required_not_recorded'
  )
  const hasMultipleBreaks = breaks.length > 1
  const shifttz = getShiftTimezone(shift)

  return (
    <>
      <Button variant={'text'} onClick={() => setVisible(true)}>
        <Icon icon={AlertCircleIcon} color={'info'} size={16} />
      </Button>
      <Modal open={visible} onClose={() => setVisible(false)}>
        <Box>
          {isLoading ? <Loading /> : null}
          {!isLoading && breaks.length === 0 ? (
            <Text>No breaks recorded.</Text>
          ) : null}
          {breaks.map((workBreak) => (
            <Box key={workBreak.breakNumber} mb={3}>
              <Heading level={4}>
                {getBreakHeading(workBreak, hasMultipleBreaks)}
              </Heading>
              {workBreak.issue ? (
                <Flex mb={2}>
                  <Icon icon={AlertTriangleIcon} color={'warning'} size={18} />
                  <Text fontWeight={2} ml={2}>
                    {BREAK_ISSUE_LABEL[workBreak.issue]}
                  </Text>
                </Flex>
              ) : null}
              <BreakDetail workBreak={workBreak} timezone={shifttz} />
              {workBreak.reason ? (
                <Text color={'lightText'}>Reason: {workBreak.reason}</Text>
              ) : null}
            </Box>
          ))}
          {!isSelfClockedOut && hasUnrecordedBreak ? (
            <Message variant={'warning'} mt={3}>
              Worker did not clock out themselves and will be reminded to fill
              out break information in the app.
            </Message>
          ) : null}
          <Message variant={'info'} mt={3} hideIcon systemLevel>
            Break information displayed is based on information reported by each
            worker via the WorkWhile app. If you believe this information is
            inaccurate or have other questions, please reach out to support.
          </Message>
        </Box>
      </Modal>
    </>
  )
}
